import React from "react";
import { Box, Typography, Chip, Paper } from "@mui/material";
import {
  Medication,
  EventAvailable,
  AccessTime,
  ThumbUp,
  ThumbDown,
  Note,
} from "@mui/icons-material";
import moment from "moment";

const formatDate = (dateStr) => {
  return dateStr ? moment(dateStr).format("DD MMM YYYY") : "N/A";
};

const formatTime = (timeStr) => {
  return timeStr ? moment(timeStr, "HH:mm:ss").format("HH:mm") : "N/A";
};

const MedicationCards = ({ medicines }) => {
  if (!medicines || medicines.length === 0) {
    return null;
  }

  const takenCount = medicines.filter((m) => m.status === "taken" || m.status === "Taken" || m.status === "Taken").length;
  const totalCount = medicines.length;

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            bgcolor: "#EFF8FF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Medication sx={{ fontSize: 18, color: "#1570EF" }} />
        </Box>
        <Typography variant="subtitle2" fontWeight={600} color="#1A1A2E">
          Medication
        </Typography>
        <Chip
          label={`${takenCount}/${totalCount}`}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.65rem",
            fontWeight: 600,
            bgcolor: takenCount === totalCount ? "#D1FAE5" : "#F1F5F9",
            color: takenCount === totalCount ? "#059669" : "#475467",
          }}
        />
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        {medicines.map((medicine) => (
          <Paper
            key={medicine.id}
            elevation={0}
            sx={{
              flex: "1 1 calc(50% - 12px)",
              minWidth: "240px",
              p: 2,
              borderRadius: "10px",
              border: "1px solid #E2E8F0",
              bgcolor: medicine.status === "taken" || medicine.status === "Taken" ? "#F0FDF4" : "#FFFFFF",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: medicine.status === "taken" || medicine.status === "Taken" ? "#86EFAC" : "#94A3B8",
              },
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
              <Typography variant="body2" fontWeight={600} color="#1A1A2E">
                {medicine.medicine_name || medicine.title || "Medication"}
              </Typography>
              <Chip
                size="small"
                label={medicine.status || "Pending"}
                sx={{
                  height: 20,
                  fontSize: "0.6rem",
                  fontWeight: 600,
                  bgcolor: medicine.status === "taken" || medicine.status === "Taken" ? "#D1FAE5" : medicine.status === "Not Given" ? "#FEF3C7" : "#F1F5F9",
                  color: medicine.status === "taken" || medicine.status === "Taken" ? "#059669" : medicine.status === "Not Given" ? "#D97706" : "#475467",
                }}
              />
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
              {medicine.dose && (
                <Chip
                  size="small"
                  label={`💊 ${medicine.dose}`}
                  sx={{
                    height: 18,
                    fontSize: "0.55rem",
                    fontWeight: 500,
                    bgcolor: "#F1F5F9",
                    color: "#475467",
                  }}
                />
              )}
              {medicine.medicine_type && (
                <Chip
                  size="small"
                  label={medicine.medicine_type}
                  sx={{
                    height: 18,
                    fontSize: "0.55rem",
                    fontWeight: 500,
                    bgcolor: "#F1F5F9",
                    color: "#475467",
                  }}
                />
              )}
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 1 }}>
              {medicine.date && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <EventAvailable sx={{ fontSize: 14, color: "#94A3B8" }} />
                  <Typography variant="caption" color="#64748B">
                    {formatDate(medicine.date)}
                  </Typography>
                </Box>
              )}
              {medicine.time && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <AccessTime sx={{ fontSize: 14, color: "#94A3B8" }} />
                  <Typography variant="caption" color="#64748B">
                    {formatTime(medicine.time)}
                  </Typography>
                </Box>
              )}
              {medicine.stock_status && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {medicine.stock_status === "In Stock" ? (
                    <ThumbUp sx={{ fontSize: 14, color: "#22C55E" }} />
                  ) : (
                    <ThumbDown sx={{ fontSize: 14, color: "#EF4444" }} />
                  )}
                  <Typography variant="caption" color="#64748B">
                    {medicine.stock_status}
                  </Typography>
                </Box>
              )}
            </Box>

            {medicine.notes && (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 1, p: 1, bgcolor: "#F8FAFC", borderRadius: 1 }}>
                <Note sx={{ fontSize: 14, color: "#94A3B8", mt: 0.25 }} />
                <Typography variant="caption" color="#64748B">
                  {medicine.notes}
                </Typography>
              </Box>
            )}

            {medicine.reason && (
              <Typography variant="caption" color="#EF4444" sx={{ display: "block", mt: 0.5 }}>
                Reason: {medicine.reason}
              </Typography>
            )}
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export default MedicationCards;